import { useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import ReactPlayer from "react-player";
import dayjs from "dayjs";
import State from "../../../../../../context";
import { Button, Flex, theme } from "antd";
import CardQuickStart from "./component/CardQuickStart";
import CardNews from "../../component/CardNews";
import CardSavedProposal from "./component/CardSavedProposal";
import ModalClientDetails from "../../component/ModalClientDetails";
import SectionAnalytic from "./component/SectionAnalytics";
import SecurityGate from "../../../../../../provider/SecurityGate";

import StylesContainer from "./style";

import { NEWS_LIST_MOCK } from "./constant";

import { addManagedUser } from "../../../../../../utils/request/manager";
import {
  getSavedProposalsAll,
  isPermissionReadonly,
} from "../../../super_admin/helper";
import { addLead } from "../../request";
import { getLatestSavedProposalProps } from "../../../../../../utils/helper/specialized";

import { ReactComponent as QuickStartClient } from "./image/quick_start_client.svg";
import { ReactComponent as QuickStartPortfolio } from "./image/quick_start_portfolio.svg";
import { ReactComponent as QuickStartProposal } from "./image/quick_start_proposal.svg";

const PageDashboard = () => {
  const [state] = useContext(State);
  const navigate = useNavigate();
  const { token } = theme.useToken();
  const [quarterAnalytics, setQuarterAnalytics] = useState({});

  const listAllProposals = getSavedProposalsAll({
    managedUsers: state.managedUsers,
    productsList: state.productsList,
  });

  useEffect(() => {
    if (!state.loading) {
      const currentYear = +dayjs().format("YYYY");
      const currentQuarter = dayjs().quarter();
      const lastQuarter = currentQuarter === 1 ? 4 : currentQuarter - 1;
      const lastQuarterYear =
        currentQuarter === 1 ? currentYear - 1 : currentYear;
      const currentQuarterProposals = listAllProposals.filter(
        it =>
          +dayjs(
            getLatestSavedProposalProps(it.valueMap.productMap)?.timestamp
          ).format("YYYY") === currentYear &&
          dayjs(
            getLatestSavedProposalProps(it.valueMap.productMap)?.timestamp
          ).quarter() === currentQuarter
      );
      const lastQuarterProposals = listAllProposals.filter(
        it =>
          it.valueMap.productMap &&
          +dayjs(
            getLatestSavedProposalProps(it.valueMap.productMap).timestamp
          ).format("YYYY") === lastQuarterYear &&
          dayjs(
            getLatestSavedProposalProps(it.valueMap.productMap).timestamp
          ).quarter() === lastQuarter
      );
      const currentQuarterConversions = currentQuarterProposals.filter(
        it =>
          it.userType === "ConvertedProspect" ||
          it.valueMap?.leadInitial?.isConverted
      );
      setQuarterAnalytics({
        currentQuarterProposals,
        currentQuarterConversions,
        lastQuarterProposals,
      });
    }
  }, [state]);

  const onClickAllProposals = () => {
    navigate(
      `/wealth-advisor-portal/proposals?email=${encodeURIComponent(state._id)}`
    );
  };

  const onClickAddClient = () => {
    state.setKeyValue("openModalClientDetails", true);
  };

  const onClickViewProposal = activeEmail => {
    const activeClientData = state.managedUsers.find(
      it => it.email === activeEmail
    );

    const isClient =
      activeClientData?.valueMap?.leadInitial?.isConverted ||
      activeClientData?.userType === "Registered";

    if (isClient) {
      // handle client
      state.setKeyValue("activeEmail", activeEmail);
      state.setKeyValue("activeSection", "client");
      navigate(
        `/wealth-advisor-portal/clients?email=${encodeURIComponent(state._id)}`
      );
    } else {
      // handle lead
      state.setKeyValue("activeEmail", activeEmail);
      state.setKeyValue("activeSection", "lead");
      navigate(
        `/wealth-advisor-portal/leads?email=${encodeURIComponent(state._id)}`
      );
    }
  };

  const submitAddClient = values => {
    if (
      values.birthYear &&
      values.startDate &&
      values.birthYear > dayjs(values.startDate?.$d).format("YYYY")
    ) {
      state.showWarning("Birth Year could not be bigger, than Start Date.");
      return;
    }

    state.setKeyValue("loading", true);

    const requestBody = {
      assets: values.assets,
      birthYear: values.birthYear,
      email: values.email,
      firstName: values.firstName,
      lastName: values.lastName,
      phoneNumber: "", // required to be passed
      retirementAge: values.retirementAge,
      scenarioName: "Scenario 1", // required to be passed
      startDate: new Date(),
      userManagerEmail: state._id,
    };

    if (values.clientType === "client") {
      addManagedUser({ managerEmail: state._id, requestBody })
        .then(() =>
          state.setUserData().then(() => {
            state.showSuccess(
              `New Client "${values.email}" Successfully Added`
            );
            state.setKeyValue("openModalClientDetails", false);
          })
        )
        .catch(error => {
          state.setKeyValue("loading", false);
          state.showError(error);
        });
    } else {
      addLead({ managerEmail: state._id, requestBody })
        .then(() =>
          state.setUserData().then(() => {
            state.showSuccess(`New Lead "${values.email}" Successfully Added`);
            state.setKeyValue("openModalClientDetails", false);
          })
        )
        .catch(error => {
          state.setKeyValue("loading", false);
          state.showError(error);
        });
    }
  };

  return (
    <SecurityGate state={state}>
      <StylesContainer token={token}>
        <div className="container-page">
          <h3 style={{ marginBottom: 24 }}>
            Welcome,{" "}
            <b>
              {state.personalInfo?.firstName} {state.personalInfo?.lastName}
            </b>
          </h3>

          <main>
            <Flex flex="1 0 696px" gap={20} style={{ zIndex: 1 }} vertical>
              <Flex vertical gap={24}>
                <Flex gap={24}>
                  <CardQuickStart
                    onClick={() =>
                      !isPermissionReadonly(
                        state.permissions?.manageProposals
                      ) && onClickAddClient()
                    }
                    Icon={QuickStartProposal}
                    style={{
                      cursor: isPermissionReadonly(
                        state.permissions?.manageProposals
                      )
                        ? "not-allowed"
                        : "pointer",
                    }}
                    title={
                      <h5>
                        <span>Create a</span>
                        <br /> New Proposal
                      </h5>
                    }
                  />
                  <CardQuickStart
                    Icon={QuickStartPortfolio}
                    title={
                      <h5>
                        <span>Build a</span>
                        <br /> New Model
                      </h5>
                    }
                  />
                  <CardQuickStart
                    onClick={() =>
                      !isPermissionReadonly(state.permissions?.manageClients) &&
                      !isPermissionReadonly(state.permissions?.manageLeads) &&
                      onClickAddClient()
                    }
                    Icon={QuickStartClient}
                    style={{
                      cursor:
                        isPermissionReadonly(
                          state.permissions?.manageProposals
                        ) ||
                        isPermissionReadonly(state.permissions?.manageLeads)
                          ? "not-allowed"
                          : "pointer",
                    }}
                    title={
                      <h5>
                        <span>Add a</span>
                        <br />
                        Client or Lead
                      </h5>
                    }
                  />
                </Flex>

                <SectionAnalytic
                  quarterAnalytics={quarterAnalytics}
                  listAllProposals={listAllProposals}
                  managedUsers={state.managedUsers}
                />

                <Flex gap={16} vertical>
                  <Flex justify="space-between">
                    <h4>Recent Proposals</h4>
                    <Button
                      onClick={onClickAllProposals}
                      shape="round"
                      size="small"
                    >
                      All Proposals
                    </Button>
                  </Flex>

                  {listAllProposals
                    ?.sort((a, b) => b.updatedAt - a.updatedAt)
                    ?.slice(0, 3)
                    ?.map((it, index) => (
                      <CardSavedProposal
                        key={index}
                        onClickViewProposal={onClickViewProposal}
                        proposalData={it}
                      />
                    ))}
                </Flex>
              </Flex>
            </Flex>

            <Flex
              flex="1 0 388px"
              className="container-marketing"
              gap={24}
              vertical
            >
              {state.settings?.video?.enabled && (
                <div>
                  <ReactPlayer
                    controls={true}
                    height={195}
                    url={state.settings?.video?.url}
                    width="100%"
                  />
                </div>
              )}

              {token.advisor_page_dashboard_show_sections_news && (
                <section className="news-section">
                  <header>
                    <h3>INDUSTRY NEWS</h3>
                  </header>

                  <Flex gap={8} vertical>
                    {NEWS_LIST_MOCK.map(it => (
                      <CardNews data={it} key={it.id} />
                    ))}
                  </Flex>

                  <Flex justify="center" style={{ padding: 16 }}>
                    <Button shape="round" size="small">
                      Load More...
                    </Button>
                  </Flex>
                </section>
              )}
            </Flex>
          </main>
        </div>

        <ModalClientDetails
          loading={state.loading}
          onCancel={() => state.setKeyValue("openModalClientDetails", false)}
          onFinish={submitAddClient}
          open={state.openModalClientDetails}
          showOptionsClientType
        />
      </StylesContainer>
    </SecurityGate>
  );
};

export default PageDashboard;
